import React, { useState, useCallback } from 'react';
import type { ProfileCreateReq } from '@interlucent/organization-protocol';
import { id3 } from '@interlucent/ids';
import { Modal, FormField } from '../shared';
import { profileService } from '../../services/profileService';
import { CapacityScheduleEditor } from './CapacityScheduleEditor';
import { InfrastructureEditor } from './InfrastructureEditor';
import { SUPER_REGION_OPTIONS } from '../../constants/regions';
import type { ProfileFormData, CapacitySchedule, InfrastructureFormData, BackendFormData } from '../../types/profile.types';

function buildSettingsJson(infra: InfrastructureFormData): Record<string, any> {
  return {
    backends: infra.backends.map((b: BackendFormData) => {
      const backend: Record<string, any> = {
        id: b.id,
        cloud: b.cloud,
        region: b.region,
        instanceType: b.instanceType,
        imageId: b.imageId,
        weight: b.weight,
      };
      if (b.maxCapacity != null) backend.maxCapacity = b.maxCapacity;
      if (b.maxConcurrent != null && b.maxConcurrent > 1) backend.maxConcurrent = b.maxConcurrent;
      if (b.startupMode && b.startupMode !== 'standard') backend.startupMode = b.startupMode;
      if (b.launchProtocolVersion != null && b.launchProtocolVersion !== 1) backend.launchProtocolVersion = b.launchProtocolVersion;
      // Free-form orc CLI args replace the individual flag knobs; persist when non-empty.
      const orchestratorArgs = b.orchestratorArgs?.trim();
      if (orchestratorArgs) backend.orchestratorArgs = orchestratorArgs;
      // Observability knobs are delivered as env vars (not CLI args), kept typed.
      if (b.streamingTarzst) backend.streamingTarzst = true;
      if (b.otelEndpoint) backend.otelEndpoint = b.otelEndpoint;
      if (b.otelAuth) backend.otelAuth = b.otelAuth;
      if (b.awsSubnets) backend.awsSubnets = b.awsSubnets.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsSecurityGroups) backend.awsSecurityGroups = b.awsSecurityGroups.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsInstanceProfile) backend.awsInstanceProfile = b.awsInstanceProfile;
      if (b.gcpProjectId) backend.gcpProjectId = b.gcpProjectId;
      if (b.gcpSubnetwork) backend.gcpSubnetwork = b.gcpSubnetwork;
      if (b.gcpFirewallTags) backend.gcpFirewallTags = b.gcpFirewallTags.split(',').map(s => s.trim()).filter(Boolean);
      if (b.gcpServiceAccount) backend.gcpServiceAccount = b.gcpServiceAccount;
      if (b.gcpGpuVariant) backend.gcpGpuVariant = b.gcpGpuVariant;
      if (b.gcpInstanceModel) backend.gcpInstanceModel = b.gcpInstanceModel;
      if (b.cwApiServer) backend.cwApiServer = b.cwApiServer;
      if (b.cwKubeToken) backend.cwKubeToken = b.cwKubeToken;
      if (b.cwNamespace) backend.cwNamespace = b.cwNamespace;
      if (b.cwGpuCount != null) backend.cwGpuCount = b.cwGpuCount;
      if (b.cwCpuCount != null) backend.cwCpuCount = b.cwCpuCount;
      if (b.cwMemoryGb != null) backend.cwMemoryGb = b.cwMemoryGb;
      if (b.cwRootDiskSize) backend.cwRootDiskSize = b.cwRootDiskSize;
      if (b.cwRootPvcLabel) {
        // Parse "key=value,key2=value2" into Record<string, string>
        const labels: Record<string, string> = {};
        b.cwRootPvcLabel.split(',').forEach(pair => {
          const [k, v] = pair.split('=').map(s => s.trim());
          if (k && v) labels[k] = v;
        });
        if (Object.keys(labels).length > 0) backend.cwRootPvcLabel = labels;
      }
      if (b.cwAdditionalDiskSize) {
        backend.cwAdditionalDisks = [{ name: 'data', size: b.cwAdditionalDiskSize }];
      }
      if (b.cwVsUsername) backend.cwVsUsername = b.cwVsUsername;
      if (b.cwVsPassword) backend.cwVsPassword = b.cwVsPassword;
      return backend;
    }),
    allocationStrategy: infra.allocationStrategy,
    // New pools default to the current shipping orc (PSO-storm fix + WS transport),
    // matching the new-private-pool wizard.
    orchestratorVersion: '0.18.3',
  };
}

function makeDefaultInfrastructure(): InfrastructureFormData {
  return {
    backends: [{
      id: `aws-useast1-${id3()}`,
      cloud: 'aws',
      region: 'us-east-1',
      instanceType: '',
      imageId: '',
      weight: 1,
    }],
    allocationStrategy: 'balanced',
  };
}

interface CreateProfileDialogProps {
  isOpen: boolean;
  projectId: string;
  isPlatformAdmin?: boolean;
  infraDefaults?: unknown;
  onClose: () => void;
  onCreate: (data: Omit<ProfileCreateReq, 'projectId'>) => Promise<{ success: boolean; error?: string }>;
}

export function CreateProfileDialog({
  isOpen,
  projectId,
  isPlatformAdmin = false,
  infraDefaults,
  onClose,
  onCreate
}: CreateProfileDialogProps) {
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    profileSlug: '',
    type: 'private_pool',
    region: 'wnam',
    notes: '',
    active: true,
    capacitySchedule: undefined,
    infrastructure: isPlatformAdmin ? makeDefaultInfrastructure() : undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((field: keyof ProfileFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = profileService.validateProfileForm(formData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await onCreate({
        name: formData.name,
        profileSlug: formData.profileSlug || undefined,
        type: formData.type,
        region: formData.region,
        notes: formData.notes || undefined,
        capacityScheduleJson: formData.capacitySchedule,
        settingsJson: formData.infrastructure ? buildSettingsJson(formData.infrastructure) : undefined,
      });

      if (result.success) {
        setFormData({
          name: '',
          profileSlug: '',
          type: 'private_pool',
          region: 'wnam',
          notes: '',
          active: true,
          capacitySchedule: undefined,
          infrastructure: isPlatformAdmin ? makeDefaultInfrastructure() : undefined,
        });
        onClose();
      } else {
        setErrors({ submit: result.error || 'Failed to create profile' });
      }
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to create profile' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onCreate, onClose]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        profileSlug: '',
        type: 'private_pool',
        region: 'wnam',
        notes: '',
        active: true,
        capacitySchedule: undefined,
        infrastructure: isPlatformAdmin ? makeDefaultInfrastructure() : undefined,
      });
      setErrors({});
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleCapacityScheduleChange = useCallback((schedule: CapacitySchedule) => {
    setFormData(prev => ({ ...prev, capacitySchedule: schedule }));
  }, []);

  const handleInfrastructureChange = useCallback((infra: InfrastructureFormData) => {
    setFormData(prev => ({ ...prev, infrastructure: infra }));
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      title="Create Dedicated Pool"
      onClose={handleClose}
      size="large"
    >
      <form onSubmit={handleSubmit}>
        {isPlatformAdmin && (
          <FormField
            label="Pool Name"
            htmlFor="profile-name"
            required
            error={errors.name}
            helperText="Descriptive name for this dedicated pool"
          >
            <input
              id="profile-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Production US, Staging EU..."
              autoFocus
              disabled={isSubmitting}
            />
          </FormField>
        )}

        <FormField
          label="Super Region"
          htmlFor="profile-region"
          required
          error={errors.region}
          helperText="Geographic super region for this pool"
        >
          <select
            id="profile-region"
            value={formData.region}
            onChange={(e) => handleChange('region', e.target.value)}
            disabled={isSubmitting}
          >
            {SUPER_REGION_OPTIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </FormField>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <CapacityScheduleEditor
            value={formData.capacitySchedule}
            onChange={handleCapacityScheduleChange}
            disabled={isSubmitting}
          />
        </div>

        {isPlatformAdmin && formData.infrastructure && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <InfrastructureEditor
              value={formData.infrastructure}
              onChange={handleInfrastructureChange}
              disabled={isSubmitting}
              infraDefaults={infraDefaults}
            />
          </div>
        )}

        {isPlatformAdmin && (
          <FormField
            label="Notes"
            htmlFor="profile-notes"
            helperText="Optional notes or description"
          >
            <textarea
              id="profile-notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes about this pool..."
              rows={3}
              disabled={isSubmitting}
            />
          </FormField>
        )}

        {errors.submit && (
          <div style={{ color: 'var(--gray-10)', marginBottom: 'var(--space-4)' }}>
            {errors.submit}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              border: 'var(--border-width) solid var(--border)',
              background: 'var(--bg-primary)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              border: 'var(--border-width) solid var(--gray-10)',
              background: 'var(--gray-10)',
              color: 'var(--gray-0)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Pool'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
